import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge, LogOut, Fuel, Home, Car, Plus, Sparkles, User, Clock,
} from "lucide-react";
import OfflineIndicator from "@/components/OfflineIndicator";
import NotificationsBell from "@/components/NotificationsBell";
import CitySelector from "@/components/CitySelector";

const linkBase =
  "px-3 py-2 text-sm font-semibold tracking-tight transition-colors duration-200 flex items-center gap-2";
const linkClass = ({ isActive }) =>
  `${linkBase} ${isActive ? "text-white bg-[color:var(--bg-elevated,#1A1A1A)] border-b-2 border-[color:var(--accent,#FF5A1F)]" : "text-[color:var(--text-secondary,#A3A3A3)] hover:text-white hover:bg-[color:var(--bg-muted,#141414)]"}`;

// Bottom tab (mobile) — icon on top, label below
const tabBase =
  "flex-1 flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold tracking-wide uppercase transition-colors duration-200";
const tabClass = ({ isActive }) =>
  `${tabBase} ${isActive ? "text-[color:var(--accent,#FF5A1F)]" : "text-[color:var(--text-secondary,#A3A3A3)] hover:text-white"}`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!(user && user.is_admin);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base,#0A0A0A)] text-[color:var(--text-primary,#FFFFFF)] grain relative">
      <header className="safe-header sticky top-0 z-30 backdrop-blur-xl bg-[color:var(--bg-base,#0A0A0A)]/85 border-b border-[color:var(--border-line,#262626)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <div
              className="w-9 h-9 flex items-center justify-center"
              style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }}
            >
              <Fuel size={20} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-lg font-black tracking-tighter">RAYFUEL</span>
              <span className="ft-overline text-[9px]">Command · Analyse · Optimise</span>
            </div>
          </NavLink>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={linkClass} data-testid="nav-home">
              <Home size={16} /> Home
            </NavLink>
            <NavLink to="/garage" className={linkClass} data-testid="nav-garage">
              <Car size={16} /> Garage
            </NavLink>
            <NavLink to="/timeline" className={linkClass} data-testid="nav-timeline">
              <Clock size={16} /> Timeline
            </NavLink>
            <NavLink to="/add" className={linkClass} data-testid="nav-add">
              <Plus size={16} /> Add
            </NavLink>
            <NavLink to="/insights" className={linkClass} data-testid="nav-insights">
              <Sparkles size={16} /> Insights
            </NavLink>
            <NavLink to="/profile" className={linkClass} data-testid="nav-profile">
              <User size={16} /> Profile
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <OfflineIndicator />
            {user && user !== false && <CitySelector value={user.preferred_city} onChange={() => {}} compact testid="header-city" />}
            {user && user !== false && <NotificationsBell />}
            <NavLink
              to="/add"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors duration-200"
              style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }}
              data-testid="cta-add"
            >
              <Plus size={16} /> Log
            </NavLink>
            {user && user !== false && (
              <button
                onClick={handleLogout}
                className="hidden md:inline-flex text-[color:var(--text-secondary,#A3A3A3)] hover:text-white text-sm items-center gap-2 px-3 py-2 hover:bg-[color:var(--bg-muted,#141414)] transition-colors duration-200"
                data-testid="logout-button"
              >
                <LogOut size={16} />
                <span className="hidden md:inline">{user.name || user.email}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="safe-main max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10 pb-28 md:pb-12">
        {children}
      </main>

      {/* Bottom Tab Bar (mobile only) — 5 tabs */}
      <nav
        className="safe-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[color:var(--bg-base,#0A0A0A)] border-t border-[color:var(--border-line,#262626)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-tab-bar"
      >
        <div className="flex items-stretch h-16 relative">
          <NavLink to="/" end className={tabClass} data-testid="tab-home">
            <Home size={20} />
            <span>Home</span>
          </NavLink>
          <NavLink to="/garage" className={tabClass} data-testid="tab-garage">
            <Car size={20} />
            <span>Garage</span>
          </NavLink>

          {/* Center FAB — Add */}
          <div className="flex-1 relative">
            <NavLink
              to="/add"
              className="absolute left-1/2 -translate-x-1/2 -top-5 w-14 h-14 flex items-center justify-center shadow-lg transition-colors"
              style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)", boxShadow: "0 8px 24px rgba(255,90,31,0.35)" }}
              aria-label="Add"
              data-testid="tab-add"
            >
              <Plus size={26} strokeWidth={2.5} />
            </NavLink>
          </div>

          <NavLink to="/timeline" className={tabClass} data-testid="tab-timeline">
            <Clock size={20} />
            <span>Timeline</span>
          </NavLink>
          <NavLink to="/profile" className={tabClass} data-testid="tab-profile">
            <User size={20} />
            <span>Profile</span>
          </NavLink>
        </div>
      </nav>

      <footer className="border-t border-[color:var(--border-line,#262626)] mt-16 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between text-[color:var(--text-tertiary,#737373)] text-xs">
          <span className="ft-overline">RAYFUEL · v0.2</span>
          <span className="font-mono flex items-center gap-2">
            <Gauge size={14} /> All values in INR (₹) & km/l
          </span>
        </div>
      </footer>
    </div>
  );
}
